import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import TextField from '@material-ui/core/TextField';
import useModelInfo from 'hooks/useModelInfo';
import { ReduxState } from 'redux/types';
import { snakeCase } from 'snake-case';
import Language from 'types/Language';

import { FlexContainer, StyledDivider } from './styled';

interface Props {
    data: any;
    handleAttributeChange: (name: string, value: any) => void;
    isLoading: boolean;
    modelName: string;
    partialTranslationKey: string;
}

const EntityTranslationsForm = (props: Props) => {
    const { data, isLoading, handleAttributeChange, modelName, partialTranslationKey } = props;

    const { translatableAttributes } = useModelInfo({ modelName });
    const languages = useSelector((state: ReduxState) => state.languages);

    const { t } = useTranslation();

    const translationsData = useMemo(() => {
        return data.translations ?? [];
    }, [data]);

    const getValue = useCallback((attribute: string, languageCode: string) => {
        const selectedData = translationsData.find((d: any) => d.attribute === attribute && d.languageCode === languageCode);

        return selectedData ? selectedData.value : '';
    }, [translationsData]);

    const setValue = useCallback((attribute: string, languageCode: string, value: string) => {
        const selectedData = translationsData.find((d: any) => d.attribute === attribute && d.languageCode === languageCode) ?? {
            attribute,
            languageCode
        };

        selectedData.value = value;

        const finalValue = [...translationsData.filter((d: any) => !(d.attribute === attribute && d.languageCode === languageCode)), selectedData];

        handleAttributeChange('translations', finalValue);
    }, [translationsData, handleAttributeChange]);

    const capitalizeFirstLetter = (str: string) => {
        if (!str || str.length < 1) return str;
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    if (!(translatableAttributes ?? []).length) return null;

    return (
        <>
            <StyledDivider />

            {languages.filter((l: Language) => l.code !== 'it-IT').map((language: Language) => {
                return (
                    <FlexContainer>
                        {translatableAttributes.map(attribute => {
                            return (
                                <TextField
                                    label={capitalizeFirstLetter(t(partialTranslationKey + '.' + snakeCase(attribute))) + ' - ' + language.description}
                                    key={language.code}
                                    value={getValue(attribute, language.code)}
                                    onChange={(e) => setValue(attribute, language.code, e.target.value)}
                                    variant='outlined'
                                    margin='dense'
                                    disabled={isLoading}
                                />
                            );
                        })}
                    </FlexContainer>
                );
            })}

        </>
    );
};

export default EntityTranslationsForm;